import { Suspense, use } from 'react'
import { format } from 'date-fns'
import { ja } from 'date-fns/locale'
import { Link, useLoaderData, useNavigate } from 'react-router'
import { BranchPageHeading } from '@/components/subscription/branch-page-heading'
import { Panel } from '~/components/subscription/panel'
import { ZendeskWidget } from '@/components/zendesk-widget'
import { formatDate, formatTime } from '@/lib/shipment'
import { findShippingStatus } from '@/lib/order'
import { createClient } from '@/api-client'
import { Me } from '@/api-client/Me'
import { Subscriptions } from '@/api-client/Subscriptions'
import { SubscriptionSchedules } from '@/api-client/SubscriptionSchedules'
import { Cards } from '@/api-client/Cards'
import { SubscriptionInventory, FlowerCollectionProduct } from '@/api-client/data-contracts'
import { InBulkCard } from './in-bulk-card'
import { AddInBulkCard } from './add-in-bulk-card'
import { RosePlanCard, PlannedMaterialEntry } from '~/components/subscription/rose-plan-card'

export const loader = async () => {
  const account = await fetchMe()
  const subscriptions = await fetchSubscriptions()
  const subscription = subscriptions.data.data[0]

  let placements = { data: { data: [] } } as Awaited<ReturnType<typeof fetchPlacements>>
  let address = { data: { data: { attributes: {} } } } as Awaited<ReturnType<typeof fetchAddress>>
  let schedules = { data: { data: [] } } as Awaited<ReturnType<typeof fetchSchedules>>
  let product = { data: { data: null } } as Awaited<ReturnType<typeof fetchProduct>>
  let inBulkPurchasables = { data: { data: [] } } as Awaited<ReturnType<typeof fetchInBulkPurchasables>>
  let orders = { data: { data: [] } } as Awaited<ReturnType<typeof fetchOrders>>
  if (subscription) {
    ;[placements, address, product, inBulkPurchasables, orders] = await Promise.all([
      fetchPlacements(subscription.id),
      fetchAddress(subscription.id),
      fetchProduct(subscription.id),
      fetchInBulkPurchasables(),
      fetchOrders(subscription.id),
    ])
    schedules = await fetchSchedules(address.data.data.attributes.postal_code)
  }

  return [account, subscriptions, placements, address, schedules, product, inBulkPurchasables, orders] as const
}

function fetchMe() {
  return createClient(Me).getMe()
}

function fetchSubscriptions() {
  return createClient(Subscriptions).getSubscriptions({ category: 'rose' })
}

function fetchPlacements(id: string) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdPlacements(id)
}

function fetchAddress(id: string) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdAddress(id)
}

function fetchSchedules(postalCode?: string) {
  return createClient(SubscriptionSchedules).getSubscriptionSchedules({ postal_code: postalCode, category: 'rose', upcoming: '1', limit: '9' })
}

function fetchProduct(id: string) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdProduct(id)
}

function fetchInBulkPurchasables() {
  return createClient(Subscriptions).getSubscriptionsInBulkPurchasables({ category: 'rose' })
}

function fetchOrders(id: string) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdOrders(id, { include: ['order_items.shipping_statuses'] })
}

function fetchDefaultCard() {
  return createClient(Cards).getCardsDefault()
}

function fetchPlannedMaterials() {
  return createClient(Subscriptions).getSubscriptionsPlannedMaterials({ category: 'rose' })
}

// 入荷予定の花材。取得に失敗してもカードを出さないだけでページは表示する
const plannedMaterialEntriesPromise = fetchPlannedMaterials()
  .then((json) => (json.data.data?.attributes?.entries ?? []) as unknown as PlannedMaterialEntry[])
  .catch(() => [] as PlannedMaterialEntry[])

const creditCardPromise = fetchDefaultCard()
  .then((res) => res.data.data)
  .catch(() => null)

export const SubscriptionRoseMypageTop: React.FC = () => {
  const [
    {
      data: { data: account },
    },
    {
      data: { data: subscriptions, included: subscriptionsIncluded = [] },
    },
    {
      data: { data: placements },
    },
    {
      data: { data: address },
    },
    {
      data: { data: schedules },
    },
    {
      data: { data: product },
    },
    {
      data: { data: inBulkPurchasables },
    },
    {
      data: { data: orders, included: ordersIncluded },
    },
  ] = useLoaderData<typeof loader>()
  const creditCard = use(creditCardPromise)
  const plannedMaterialEntries: PlannedMaterialEntry[] = use(plannedMaterialEntriesPromise)
  const navigate = useNavigate()

  const subscription = subscriptions[0]
  const nextPlacement = placements[0]
  const orderableName = product?.attributes.name
  const inBulkProducts = subscriptionsIncluded.filter((item) => {
    return item.type === 'flower_collection_product' && item.attributes.bulk_discounted_price > 0
  }) as unknown as FlowerCollectionProduct[]
  const date = subscription && new Date(subscription.attributes.next_deliver_at * 1000)
  const weekday = date ? format(date, 'E', { locale: ja }) : ''
  const currentSchedules =
    subscription?.relationships.inventories.data
      .map((inventory: SubscriptionInventory) => schedules.find((s) => s.relationships.inventory.data.id === inventory.id))
      .sort((a, b) => a.attributes.deliver_at[0] - b.attributes.deliver_at[0]) || []
  const latestShipping = findShippingStatus(orders[0], ordersIncluded)
  const latestShippingStatus = latestShipping?.attributes?.status

  const handleClickSkip: React.MouseEventHandler<HTMLButtonElement> = async () => {
    try {
      await createClient(Subscriptions).postSubscriptionsSubscriptionIdPlacementsIdSkip(subscription.id, nextPlacement.id)
      await navigate('/subscription/rose/mypage', { state: { message: '次回のお届けをスキップしました' } })
    } catch (error) {
      console.error(error)
    }
  }

  const handleClickUnskip: React.MouseEventHandler<HTMLButtonElement> = async () => {
    try {
      await createClient(Subscriptions).postSubscriptionsSubscriptionIdPlacementsIdUnskip(subscription.id, nextPlacement.id)
      await navigate('/subscription/rose/mypage', { state: { message: '次回のお届けのスキップを取り消しました' } })
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <>
      <div className="flex flex-col gap-6 items-start w-full">
        <div className="flex flex-col gap-1">
          <BranchPageHeading
            main="マイページ"
            sub={subscription && `${formatTime(1000 * subscription.attributes.skip_deadline)}まで今後のお届け分のスキップ・配送先などの変更が可能です`}
          />
        </div>
        {subscription ? (
          <>
            {['not_shipped', 'preparation', 'transit'].includes(latestShippingStatus) && (
              <Panel>
                <h3 className="text-[15px] font-bold text-black leading-relaxed">次回の到着日</h3>
                <div className="flex items-center gap-1">
                  <span className="text-[19px] font-bold text-black leading-relaxed">
                    {formatDate(1000 * latestShipping.attributes.deliver_at, 'M/d（E）')}
                  </span>
                  <span className="inline-block bg-[#eeeeee] rounded-sm px-1.5 py-0.5 text-[12px] font-bold">{latestShipping.attributes.status_label}</span>
                </div>
                {latestShipping?.attributes?.tracking_code_url && (
                  <a href={latestShipping.attributes.tracking_code_url} className="inline-block px-4 py-2 bg-black text-white text-[13px] font-bold rounded-full">
                    追跡する
                  </a>
                )}
              </Panel>
            )}

            <Panel>
              <h3 className="text-[15px] font-bold text-black leading-relaxed">今後のお届け予定</h3>
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-[19px] font-bold text-black leading-relaxed ${nextPlacement?.attributes?.status === 'skipped' ? 'line-through' : ''}`}
                    >
                      {currentSchedules[0] && formatDate(currentSchedules[0].attributes.deliver_at[0] * 1000, 'M/d（E）')}
                    </span>
                    {nextPlacement?.attributes?.status === 'skipped' && (
                      <button
                        type="button"
                        onClick={handleClickUnskip}
                        className="text-[13px] font-bold text-black underline decoration-solid leading-relaxed tracking-wide"
                      >
                        スキップを取り消す
                      </button>
                    )}
                    {nextPlacement?.attributes?.status === 'pending' && (
                      <button
                        type="button"
                        onClick={handleClickSkip}
                        className="text-[13px] font-bold text-black underline decoration-solid leading-relaxed tracking-wide"
                      >
                        お届けをスキップ
                      </button>
                    )}
                  </div>

                  {currentSchedules.length > 1 && (
                    <div className="flex flex-col gap-1">
                      {currentSchedules.slice(1).map((schedule) => (
                        <p key={schedule.id} className="text-[15px] font-bold text-black leading-relaxed">
                          {formatDate(schedule?.attributes.deliver_at[0] * 1000, 'M/d（E）')}
                        </p>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-0.5 text-[#828282]">
                  <div className="flex gap-1 items-start">
                    <span className="mt-0.5">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M1.5 4.5l5.5 3 5.5-3" />
                        <path d="M7 13V7.5" />
                        <path d="M12.5 4.5l-5.5-3-5.5 3v5l5.5 3 5.5-3v-5z" />
                      </svg>
                    </span>
                    <p className="text-[13px] leading-relaxed tracking-wide">クロネコヤマトで配送（対面でのお受け取り）</p>
                  </div>
                </div>

                {subscription.relationships.inventories.data.length === 1 && (
                  <div>
                    <Link to="/subscription/rose/mypage/next" className="inline-block px-4 py-2 bg-black text-white text-[13px] font-bold rounded-full">
                      お届け日の変更
                    </Link>
                  </div>
                )}
              </div>
            </Panel>
          </>
        ) : (
          <Panel>
            <p className="text-[14px] text-black leading-relaxed tracking-wide">ご契約中の定期便はありません</p>
            <div>
              <Link to="/subscription/rose/new" className="inline-block px-4 py-2 bg-black text-white text-[13px] font-bold rounded-full">
                定期便を申し込む
              </Link>
            </div>
          </Panel>
        )}

        {plannedMaterialEntries.length > 0 && <RosePlanCard entries={plannedMaterialEntries} />}

        <InBulkCard subscription={subscription} product={product} inBulkProducts={inBulkProducts} placements={placements} />
        <AddInBulkCard subscription={subscription} nextBulkProducts={inBulkProducts} inBulkProducts={inBulkPurchasables} />

        {subscription && (
          <Panel>
            <h3 className="text-[15px] font-bold text-black leading-relaxed">お届けスケジュール</h3>
            <div className="flex flex-col items-start gap-2">
              <p className="text-[13px] text-black leading-relaxed tracking-wide">
                月に{currentSchedules.length}回・{weekday}曜日にお届け
              </p>
              <Link to="/subscription/rose/mypage/schedule" className="inline-block px-4 py-2 bg-black text-white text-[13px] font-bold rounded-full">
                変更する
              </Link>
            </div>
          </Panel>
        )}

        <Panel>
          <h3 className="text-[15px] font-bold text-black leading-relaxed">お届け履歴</h3>
          <div className="flex flex-col items-start gap-2">
            <p className="text-[13px] text-black leading-relaxed tracking-wide">これまでのお届け内容を確認できます</p>
            <Link to="/subscription/rose/mypage/orders" className="inline-block px-4 py-2 bg-black text-white text-[13px] font-bold rounded-full">
              履歴を見る
            </Link>
          </div>
        </Panel>

        {subscription && (
          <Panel>
            <h3 className="text-[15px] font-bold text-black leading-relaxed">お届け先</h3>
            <div className="flex flex-col items-start gap-2">
              <div className="text-[13px] text-black leading-relaxed tracking-wide">
                {address?.attributes ? (
                  <>
                    <p>〒{address.attributes.postal_code}</p>
                    <p>
                      {address.attributes.state}
                      {address.attributes.city_and_street}
                    </p>
                    <p>{address.attributes.others}</p>
                    <p>
                      {address.attributes.name}
                      {!!address.attributes.sender_name ? `（${address.attributes.sender_name}より）` : ''}
                    </p>
                  </>
                ) : (
                  <p>登録されていません</p>
                )}
              </div>
              <Link to="/subscription/rose/mypage/address" className="inline-block px-4 py-2 bg-black text-white text-[13px] font-bold rounded-full">
                お届け先を変更する
              </Link>
            </div>
          </Panel>
        )}

        <Panel>
          <p className="text-[15px] font-bold text-black leading-relaxed">メールアドレス</p>
          <div className="flex flex-col gap-2">
            <p className="text-[13px] text-black leading-relaxed tracking-wide">{account.attributes.email}</p>

            <div className="flex flex-col gap-2 items-start">
              <Link to="/subscription/rose/mypage/email" className="inline-block px-4 py-2 bg-black text-white text-[13px] font-bold rounded-full">
                メールアドレスを変更する
              </Link>
              <Link to="/subscription/rose/mypage/password" className="inline-block px-4 py-2 bg-black text-white text-[13px] font-bold rounded-full">
                パスワードを変更する
              </Link>
            </div>
          </div>
        </Panel>

        <Panel>
          <p className="text-[15px] font-bold text-black leading-relaxed">クレジットカード</p>
          <div className="flex flex-col gap-2">
            <Suspense fallback={<></>}>
              {creditCard ? (
                <p className="text-[13px] text-black leading-relaxed tracking-wide">**** **** **** {creditCard.attributes?.last4}</p>
              ) : (
                <p className="text-[13px] text-black leading-relaxed tracking-wide">登録されていません</p>
              )}
            </Suspense>

            <div>
              <Link to="/subscription/rose/mypage/card" className="inline-block px-4 py-2 bg-black text-white text-[13px] font-bold rounded-full">
                クレジットカードを変更する
              </Link>
            </div>
          </div>
        </Panel>
      </div>
      <div className="flex flex-col gap-6 items-center">
        <Link to="/subscription/rose/mypage/cancel" className="text-[15px] text-black underline decoration-solid leading-relaxed">
          定期便の解約
        </Link>
        <Link to="/subscription/rose/mypage/delete" className="text-[15px] text-black underline decoration-solid leading-relaxed">
          アカウントを削除
        </Link>
      </div>

      <ZendeskWidget orderableName={orderableName} userId={account?.id} userEmail={account?.attributes.email} />
    </>
  )
}
